import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const CHAPTER_RANGE_SIZE = 20;

function clean(value) {
  return String(
    value ?? ""
  ).trim();
}

function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}

function countLabel(
  count,
  singular,
  plural
) {
  const value =
    Number(count) || 0;

  return `${value} ${
    value === 1
      ? singular
      : plural
  }`;
}

function countWords(
  value
) {
  const text =
    String(
      value ?? ""
    ).trim();

  return text
    ? text
        .split(/\s+/)
        .filter(Boolean)
        .length
    : 0;
}

function manuscriptWords(
  content
) {
  return safeArray(
    content?.blocks
  ).reduce(
    (
      total,
      block
    ) =>
      total +
      safeArray(
        block?.runs
      ).reduce(
        (
          runTotal,
          run
        ) =>
          runTotal +
          countWords(
            run?.text
          ),
        0
      ),
    0
  );
}

export function textbookChapterWordCount(
  chapterDraft
) {
  return safeArray(
    chapterDraft?.blocks
  ).reduce(
    (
      total,
      block
    ) => {
      const richWords =
        manuscriptWords(
          block
            ?.manuscriptContent
        );

      if (richWords > 0) {
        return (
          total +
          richWords
        );
      }

      if (
        clean(block?.type) ===
        "figure"
      ) {
        return total;
      }

      if (
        clean(block?.type) ===
        "table"
      ) {
        return (
          total +
          countWords(
            block
              ?.table
              ?.caption
          ) +
          safeArray(
            block
              ?.table
              ?.rows
          ).reduce(
            (
              rowTotal,
              row
            ) =>
              rowTotal +
              safeArray(
                row
              ).reduce(
                (
                  cellTotal,
                  cell
                ) =>
                  cellTotal +
                  countWords(
                    cell
                  ),
                0
              ),
            0
          )
        );
      }

      if (
        clean(block?.type) ===
        "equation"
      ) {
        return (
          total +
          countWords(
            block
              ?.equation
              ?.source ||
            block?.text
          )
        );
      }

      return (
        total +
        countWords(
          block?.text
        )
      );
    },
    0
  );
}

export function buildTextbookWritingPlan(
  bookMap = {}
) {
  const items =
    safeArray(
      bookMap?.items
    )
      .map(
        (item) => ({
          itemId:
            clean(
              item?.itemId
            ),

          type:
            clean(
              item?.type
            ),

          title:
            clean(
              item?.title
            ),

          parentItemId:
            clean(
              item
                ?.parentItemId
            ),
        })
      )
      .filter(
        (item) =>
          item.itemId
      );

  const rawChapters =
    items.filter(
      (item) =>
        item.type ===
        "chapter"
    );

  const chapterNo =
    new Map(
      rawChapters.map(
        (
          chapter,
          index
        ) => [
          chapter.itemId,
          index + 1,
        ]
      )
    );

  const partsById =
    new Map(
      items
        .filter(
          (item) =>
            item.type ===
            "part"
        )
        .map(
          (part) => [
            part.itemId,
            part,
          ]
        )
    );

  const sectionsFor =
    (chapterId) =>
      items
        .filter(
          (item) =>
            item.type ===
              "section" &&
            item
              .parentItemId ===
              chapterId
        )
        .map(
          (
            section,
            index
          ) => ({
            ...section,

            sectionNumber:
              `${
                chapterNo.get(
                  chapterId
                )
              }.${index + 1}`,
          })
        );

  const chapters =
    rawChapters.map(
      (chapter) => ({
        ...chapter,

        chapterNumber:
          chapterNo.get(
            chapter.itemId
          ),

        sections:
          sectionsFor(
            chapter.itemId
          ),

        part:
          partsById.get(
            chapter
              .parentItemId
          ) ||
          null,
      })
    );

  const parts =
    items
      .filter(
        (item) =>
          item.type ===
            "part" &&
          !item.parentItemId
      )
      .map(
        (
          part,
          index
        ) => ({
          ...part,

          partNumber:
            index + 1,

          chapters:
            chapters.filter(
              (chapter) =>
                chapter
                  .parentItemId ===
                part.itemId
            ),
        })
      );

  const standaloneChapters =
    chapters.filter(
      (chapter) =>
        !chapter
          .parentItemId
    );

  return {
    chapters,
    parts,
    standaloneChapters,
    chapterCount:
      chapters.length,
    sectionCount:
      items.filter(
        (item) =>
          item.type ===
          "section"
      ).length,
  };
}

function rangeStorageKey(
  projectId
) {
  return [
    "nextscenes",
    "textbook",
    clean(projectId),
    "chapter-range-start",
  ].join(":");
}

function initialRangeStart(
  projectId
) {
  try {
    if (
      typeof window ===
        "undefined" ||
      !window.sessionStorage
    ) {
      return 0;
    }

    const value =
      Number(
        window
          .sessionStorage
          .getItem(
            rangeStorageKey(
              projectId
            )
          )
      );

    return (
      Number.isInteger(
        value
      ) &&
      value >= 0
    )
      ? value
      : 0;
  } catch {
    return 0;
  }
}

function statusPresentation({
  state,
  words,
  isFR,
}) {
  if (
    state?.loading
  ) {
    return {
      label:
        isFR
          ? "Chargement"
          : "Loading",
      background:
        "#fef3c7",
      color:
        "#92400e",
    };
  }

  if (
    clean(
      state?.error
    )
  ) {
    return {
      label:
        isFR
          ? "État indisponible"
          : "Status unavailable",
      background:
        "#fef3c7",
      color:
        "#92400e",
    };
  }

  if (words > 0) {
    return {
      label:
        isFR
          ? "En cours"
          : "In progress",
      background:
        "#dcfce7",
      color:
        "#166534",
    };
  }

  return {
    label:
      isFR
        ? "Non commencé"
        : "Not started",
    background:
      "#fef3c7",
    color:
      "#92400e",
  };
}

function ChapterCard({
  chapter,
  state,
  onOpenChapter,
  canEdit,
  isFR,
}) {
  const words =
    textbookChapterWordCount(
      state?.chapterDraft
    );

  const status =
    statusPresentation({
      state,
      words,
      isFR,
    });

  const sectionCount =
    safeArray(
      chapter?.sections
    ).length;

  const chapterNumber =
    Number(
      chapter?.chapterNumber
    ) || 0;

  const fallbackTitle =
    isFR
      ? `Chapitre ${chapterNumber}`
      : `Chapter ${chapterNumber}`;

  const displayTitle =
    clean(
      chapter?.title
    ) ||
    fallbackTitle;

  const normalizedTitle =
    displayTitle
      .toLowerCase();

  const genericNumberedTitle =
    normalizedTitle ===
      `chapter ${chapterNumber}` ||
    normalizedTitle ===
      `chapitre ${chapterNumber}`;

  const wordSummary =
    words > 0
      ? (
          isFR
            ? `${words.toLocaleString()} mot(s)`
            : `${words.toLocaleString()} word(s)`
        )
      : "";

  return (
    <article
      id={
        `textbook-write-chapter-${chapter.itemId}`
      }
      data-textbook-chapter-card="true"
      data-textbook-chapter-state={
        words > 0
          ? "in-progress"
          : "not-started"
      }
      className="ns-card"
      style={{
        padding: 16,
        borderRadius: 16,
        border:
          "1px solid rgba(148,163,184,0.30)",
        background:
          "#ffffff",
        boxShadow:
          "0 7px 18px rgba(15,23,42,0.055)",
        display: "flex",
        flexDirection:
          "column",
        gap: 11,
        minHeight: 138,
        scrollMarginTop: 180,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "flex-start",
          gap: 12,
        }}
      >
        <div
          style={{
            minWidth: 0,
          }}
        >
          {!genericNumberedTitle ? (
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 900,
                letterSpacing:
                  "0.08em",
                textTransform:
                  "uppercase",
                color: "#047857",
              }}
            >
              {isFR
                ? `CHAPITRE ${chapterNumber}`
                : `CHAPTER ${chapterNumber}`}
            </div>
          ) : null}

          <h4
            className="ns-card-title ns-textbook-academic-title"
            style={{
              margin:
                genericNumberedTitle
                  ? 0
                  : "4px 0 0",
              fontSize: 20,
              lineHeight: 1.25,
            }}
          >
            {displayTitle}
          </h4>
        </div>

        <span
          aria-label={
            isFR
              ? "État du chapitre"
              : "Chapter status"
          }
          style={{
            flex:
              "0 0 auto",
            display:
              "inline-flex",
            padding:
              "5px 9px",
            borderRadius:
              999,
            fontSize: 11.5,
            fontWeight:
              900,
            background:
              status.background,
            color:
              status.color,
          }}
        >
          {status.label}
        </span>
      </div>

      <div
        className="ns-card-meta"
        data-textbook-chapter-summary="true"
        style={{
          lineHeight: 1.45,
          fontWeight: 750,
          color: "#475569",
        }}
      >
        {countLabel(
          sectionCount,
          "section",
          "sections"
        )}
        {wordSummary
          ? ` · ${wordSummary}`
          : ""}
      </div>

      <button
        className={
          canEdit &&
          words > 0
            ? "ns-button ns-button-primary"
            : "ns-button ns-button-outline"
        }
        type="button"
        onClick={() =>
          onOpenChapter?.(
            chapter.itemId
          )
        }
        style={{
          alignSelf:
            "flex-start",
          marginTop: "auto",
        }}
      >
        {!canEdit
          ? (
              isFR
                ? "Ouvrir le chapitre"
                : "Open Chapter"
            )
          : words > 0
            ? (
                isFR
                  ? "Continuer"
                  : "Continue Writing"
              )
            : (
                isFR
                  ? "Commencer"
                  : "Start Writing"
              )}
      </button>
    </article>
  );
}

export default function TextbookWriteChapters({
  project,
  projectPlan,
  onOpenChapter,
  onOpenBookStructure,
  onLoadChapterDraft,
  canEdit = true,
  isFR = false,
}) {
  const projectId =
    clean(project?.id);

  const plan =
    useMemo(
      () =>
        buildTextbookWritingPlan(
          projectPlan
            ?.bookMap
        ),
      [
        projectPlan
          ?.bookMap,
      ]
    );

  const [
    rangeStart,
    setRangeStart,
  ] = useState(
    () =>
      initialRangeStart(
        projectId
      )
  );

  const [
    chapterStates,
    setChapterStates,
  ] = useState({});

  const pendingScrollId =
    useRef("");

  const maxRangeStart =
    Math.max(
      0,
      Math.floor(
        Math.max(
          0,
          plan.chapterCount - 1
        ) /
        CHAPTER_RANGE_SIZE
      ) *
      CHAPTER_RANGE_SIZE
    );

  const boundedRangeStart =
    Math.min(
      Math.max(
        0,
        rangeStart
      ),
      maxRangeStart
    );

  const visibleChapters =
    useMemo(
      () =>
        plan.chapters.slice(
          boundedRangeStart,
          boundedRangeStart +
            CHAPTER_RANGE_SIZE
        ),
      [
        plan.chapters,
        boundedRangeStart,
      ]
    );

  useEffect(() => {
    if (
      boundedRangeStart !==
      rangeStart
    ) {
      setRangeStart(
        boundedRangeStart
      );
    }
  }, [
    boundedRangeStart,
    rangeStart,
  ]);

  useEffect(() => {
    try {
      if (
        typeof window !==
          "undefined" &&
        window.sessionStorage &&
        projectId
      ) {
        window
          .sessionStorage
          .setItem(
            rangeStorageKey(
              projectId
            ),
            String(
              boundedRangeStart
            )
          );
      }
    } catch {
      // Range memory is convenience only.
    }
  }, [
    boundedRangeStart,
    projectId,
  ]);

  useEffect(() => {
    const targetId =
      clean(
        pendingScrollId
          .current
      );

    if (!targetId) {
      return;
    }

    const target =
      typeof document !==
        "undefined"
        ? document
            .getElementById(
              `textbook-write-chapter-${targetId}`
            )
        : null;

    if (!target) {
      return;
    }

    pendingScrollId.current =
      "";

    target
      .scrollIntoView?.({
        behavior:
          "smooth",
        block:
          "center",
      });
  }, [
    boundedRangeStart,
    visibleChapters,
  ]);

  useEffect(() => {
    let cancelled =
      false;

    if (
      !projectId ||
      typeof onLoadChapterDraft !==
        "function"
    ) {
      return () => {
        cancelled = true;
      };
    }

    const chaptersToLoad =
      visibleChapters;

    if (
      chaptersToLoad.length ===
      0
    ) {
      return () => {
        cancelled = true;
      };
    }

    setChapterStates(
      (current) => {
        const next = {
          ...current,
        };

        chaptersToLoad.forEach(
          (chapter) => {
            next[
              chapter.itemId
            ] = {
              loading: true,
              error: "",
              chapterDraft:
                current[
                  chapter.itemId
                ]?.chapterDraft ||
                null,
            };
          }
        );

        return next;
      }
    );

    (
      async () => {
        const results =
          await Promise.all(
            chaptersToLoad.map(
              async (
                chapter
              ) => {
                try {
                  const payload =
                    await onLoadChapterDraft(
                      projectId,
                      chapter.itemId
                    );

                  return [
                    chapter
                      .itemId,
                    {
                      loading:
                        false,
                      error: "",
                      chapterDraft:
                        payload
                          ?.chapterDraft ||
                        null,
                    },
                  ];
                } catch (
                  error
                ) {
                  return [
                    chapter
                      .itemId,
                    {
                      loading:
                        false,
                      error:
                        clean(
                          error
                            ?.message
                        ) ||
                        (
                          isFR
                            ? "État indisponible."
                            : "Status unavailable."
                        ),
                      chapterDraft:
                        null,
                    },
                  ];
                }
              }
            )
          );

        if (cancelled) {
          return;
        }

        setChapterStates(
          (current) => ({
            ...current,
            ...Object
              .fromEntries(
                results
              ),
          })
        );
      }
    )();

    return () => {
      cancelled = true;
    };
  }, [
    projectId,
    visibleChapters,
    onLoadChapterDraft,
    isFR,
  ]);

  function jumpToChapter(
    chapterId
  ) {
    const index =
      plan.chapters
        .findIndex(
          (chapter) =>
            chapter
              .itemId ===
            chapterId
        );

    if (index < 0) {
      return;
    }

    pendingScrollId.current =
      chapterId;

    setRangeStart(
      Math.floor(
        index /
        CHAPTER_RANGE_SIZE
      ) *
      CHAPTER_RANGE_SIZE
    );
  }

  const rangeEnd =
    Math.min(
      plan.chapterCount,
      boundedRangeStart +
        visibleChapters.length
    );

  let previousPartId =
    null;

  return (
    <section
      className="ns-card"
      data-textbook-nbb-chapter-map="true"
      style={{
        padding: 22,
        borderRadius: 20,
        border:
          "1.5px solid rgba(5,150,105,0.24)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          gap: 14,
          flexWrap:
            "wrap",
          alignItems:
            "flex-start",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.1em",
              textTransform:
                "uppercase",
              color: "#047857",
            }}
          >
            {isFR
              ? "ÉCRIRE"
              : "WRITE"}
          </div>

          <h3
            className="ns-card-title ns-textbook-academic-title"
            style={{
              margin:
                "6px 0 8px",
              fontSize: 26,
            }}
          >
            {clean(
              project?.title
            ) ||
              (
                isFR
                  ? "Manuel sans titre"
                  : "Untitled Textbook"
              )}
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              lineHeight: 1.6,
              maxWidth: 760,
            }}
          >
            {isFR
              ? "Choisissez un chapitre pour commencer ou continuer la rédaction. La Structure du livre contrôle l’ordre du manuel."
              : "Choose a Chapter to start or continue writing. Book Structure controls the order of the textbook."}
          </p>
        </div>

        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={
            onOpenBookStructure
          }
        >
          {isFR
            ? "Gérer les chapitres"
            : "Manage Chapters"}
        </button>
      </div>

      <div
        className="ns-card ns-card-soft"
        style={{
          marginTop: 16,
          padding:
            "12px 14px",
          borderRadius: 13,
          display: "grid",
          gap: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            gap: 12,
            flexWrap:
              "wrap",
            alignItems:
              "center",
          }}
        >
          <strong>
            {plan.chapterCount
              ? (
                  isFR
                    ? `Chapitres ${boundedRangeStart + 1}–${rangeEnd} sur ${plan.chapterCount}`
                    : `Chapters ${boundedRangeStart + 1}–${rangeEnd} of ${plan.chapterCount}`
                )
              : (
                  isFR
                    ? "Aucun chapitre"
                    : "No Chapters"
                )}
          </strong>

          <span
            className="ns-card-meta"
          >
            {isFR
              ? `${countLabel(plan.chapterCount, "chapitre", "chapitres")} · ${countLabel(plan.sectionCount, "section", "sections")}`
              : `${countLabel(plan.chapterCount, "chapter", "chapters")} · ${countLabel(plan.sectionCount, "section", "sections")}`}
          </span>
        </div>

        {plan.chapterCount >
        1 ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                plan.chapterCount >
                  CHAPTER_RANGE_SIZE
                  ? "auto minmax(220px, 1fr) auto"
                  : "minmax(220px, 1fr)",
              gap: 10,
              alignItems:
                "end",
            }}
          >
            {plan.chapterCount >
            CHAPTER_RANGE_SIZE ? (
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  boundedRangeStart ===
                  0
                }
                onClick={() =>
                  setRangeStart(
                    Math.max(
                      0,
                      boundedRangeStart -
                        CHAPTER_RANGE_SIZE
                    )
                  )
                }
              >
                {isFR
                  ? "Plage précédente"
                  : "Previous Range"}
              </button>
            ) : null}

            <label
              style={{
                display: "grid",
                gap: 6,
                fontWeight: 800,
              }}
            >
              <span>
                {isFR
                  ? "Aller à un chapitre"
                  : "Jump to Chapter"}
              </span>

              <select
                aria-label={
                  isFR
                    ? "Aller à un chapitre"
                    : "Jump to Chapter"
                }
                value=""
                onChange={(
                  event
                ) => {
                  const value =
                    clean(
                      event
                        .target
                        .value
                    );

                  if (value) {
                    jumpToChapter(
                      value
                    );
                  }
                }}
                style={{
                  minHeight: 44,
                  padding:
                    "10px 12px",
                  borderRadius:
                    10,
                  border:
                    "1px solid #cbd5e1",
                  background:
                    "#fff",
                  font: "inherit",
                }}
              >
                <option value="">
                  {isFR
                    ? "Choisir un chapitre…"
                    : "Choose a Chapter…"}
                </option>

                {plan.chapters.map(
                  (chapter) => (
                    <option
                      key={
                        chapter.itemId
                      }
                      value={
                        chapter.itemId
                      }
                    >
                      {isFR
                        ? `Chapitre ${chapter.chapterNumber} · ${chapter.title || "Sans titre"}`
                        : `Chapter ${chapter.chapterNumber} · ${chapter.title || "Untitled"}`}
                    </option>
                  )
                )}
              </select>
            </label>

            {plan.chapterCount >
            CHAPTER_RANGE_SIZE ? (
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  boundedRangeStart >=
                  maxRangeStart
                }
                onClick={() =>
                  setRangeStart(
                    Math.min(
                      maxRangeStart,
                      boundedRangeStart +
                        CHAPTER_RANGE_SIZE
                    )
                  )
                }
              >
                {isFR
                  ? "Plage suivante"
                  : "Next Range"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div
        data-textbook-chapter-card-grid="true"
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 16,
          marginTop: 18,
        }}
      >
        {visibleChapters.map(
          (chapter) => {
            const currentPartId =
              clean(
                chapter
                  ?.part
                  ?.itemId
              );

            const showPartHeading =
              currentPartId &&
              currentPartId !==
                previousPartId;

            previousPartId =
              currentPartId;

            return (
              <React.Fragment
                key={
                  chapter.itemId
                }
              >
                {showPartHeading ? (
                  <div
                    data-textbook-part-heading="true"
                    style={{
                      gridColumn:
                        "1 / -1",
                      padding:
                        "4px 2px 0",
                      borderBottom:
                        "1px solid rgba(5,150,105,0.18)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight:
                          900,
                        letterSpacing:
                          "0.1em",
                        textTransform:
                          "uppercase",
                        color:
                          "#047857",
                      }}
                    >
                      {isFR
                        ? "PARTIE / UNITÉ"
                        : "PART / UNIT"}
                    </div>

                    <div
                      className="ns-textbook-academic-title"
                      style={{
                        margin:
                          "3px 0 6px",
                        fontSize: 18,
                        lineHeight: 1.2,
                      }}
                    >
                      {chapter
                        .part
                        .title ||
                        (
                          isFR
                            ? "Sans titre"
                            : "Untitled"
                        )}
                    </div>
                  </div>
                ) : null}

                <ChapterCard
                  chapter={chapter}
                  state={
                    chapterStates[
                      chapter
                        .itemId
                    ] || null
                  }
                  onOpenChapter={
                    onOpenChapter
                  }
                  canEdit={
                    canEdit
                  }
                  isFR={isFR}
                />
              </React.Fragment>
            );
          }
        )}
      </div>
    </section>
  );
}
